import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Coffee, Sun, Cookie, Moon } from "lucide-react";
import { todayStr, fmtDay } from "@/lib/utils";
import HistoryStamps from "@/components/HistoryStamps";
import PrintMenuBoard from "@/components/PrintMenuBoard";

const SERVICES = [
  { key: "Breakfast", icon: Coffee },
  { key: "Lunch", icon: Sun },
  { key: "Snacks", icon: Cookie },
  { key: "Dinner", icon: Moon },
];

export const fmt12 = (t) => {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

export default function MenuViewer({ cafeterias = [], title, reloadKey = 0, renderActions, showCafeterias = true, jumpTo }) {
  const [date, setDate] = useState(todayStr());
  const [cafId, setCafId] = useState("all");
  const [menus, setMenus] = useState([]);

  useEffect(() => {
    if (jumpTo) setDate(jumpTo);
  }, [jumpTo, reloadKey]);

  useEffect(() => {
    let live = true;
    const params = { date };
    if (cafId !== "all") params.cafeteria_id = cafId;
    api.get("/menus", { params }).then((r) => { if (live) setMenus(r.data); }).catch(() => { if (live) setMenus([]); });
    return () => { live = false; };
  }, [date, cafId, reloadKey]);

  const isToday = date === todayStr();
  const selCaf = cafeterias.find((c) => c.id === cafId);
  const place = selCaf ? selCaf.name.trim() : (cafeterias.length ? "All cafeterias" : title || "Cafeteria");

  return (
    <div className="space-y-5" data-testid="menu-viewer">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="menu-title">
            <BookOpen className="h-6 w-6 text-primary" /> {isToday ? "Today's Menu" : "Menu Archive"}
          </h2>
          <p className="text-sm text-muted-foreground mt-1" data-testid="menu-subtitle">{title ? `${title} · ` : ""}{fmtDay(date)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cafeterias.length > 0 && (
            <Select value={cafId} onValueChange={setCafId}>
              <SelectTrigger className="w-56" data-testid="menu-cafeteria-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cafeterias</SelectItem>
                {cafeterias.map((c) => <SelectItem key={c.id} value={c.id}>{c.name.trim()}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value || todayStr())} className="w-44 font-mono" data-testid="menu-date-input" />
          <Button size="sm" variant={isToday ? "default" : "outline"} onClick={() => setDate(todayStr())} data-testid="menu-today-button">Today</Button>
          <PrintMenuBoard place={place} date={date} menus={menus} />
        </div>
      </div>

      {menus.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="menu-empty">No menu planned for {fmtDay(date)}.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mobile-compact">
          {SERVICES.filter((s) => menus.some((m) => m.service === s.key)).map(({ key, icon: Icon }) => (
            <Card key={key} className="p-5 border-orange-100 dark:border-slate-800 fade-up" data-testid={`menu-service-${key}`}>
              <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-3"><Icon className="h-5 w-5 text-primary" /> {key}</h3>
              <ul className="divide-y divide-border">
                {menus.filter((m) => m.service === key).map((m) => (
                  <li key={m.id} className="py-3" data-testid="menu-entry">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold" data-testid="menu-entry-item">{m.item_name}</p>
                        {m.description && <p className="text-sm text-muted-foreground mt-0.5" data-testid="menu-entry-description">{m.description}</p>}
                      </div>
                      <Badge variant="outline" className="font-mono shrink-0" data-testid="menu-entry-time">{fmt12(m.time)}</Badge>
                    </div>
                    {showCafeterias && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {m.cafeteria_names.map((n) => <Badge key={n} variant="secondary" className="text-[11px]">{n}</Badge>)}
                      </div>
                    )}
                    {renderActions && renderActions(m)}
                    {renderActions && <HistoryStamps history={m.history} testid="menu-entry-history" />}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
